import { OrdersAvailableReportRow } from '../model/orders-available.model';

/** Comisión (10%) — el % siempre, también si se ingresó un monto fijo. */
export function formatOrderCommissionLabel(input: {
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  baseAmount?: number | null;
}): string {
  const percent = resolveCommissionPercent(input);
  if (percent == null) return 'Comisión';
  return `Comisión (${formatPercent(percent)}%)`;
}

function resolveCommissionPercent(input: {
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  baseAmount?: number | null;
}): number | null {
  if (input.commissionPercent != null && Number.isFinite(Number(input.commissionPercent))) {
    return Number(input.commissionPercent);
  }
  const base = Number(input.baseAmount);
  const amount = Number(input.commissionAmount);
  if (Number.isFinite(base) && base > 0 && Number.isFinite(amount)) {
    return (amount / base) * 100;
  }
  return null;
}

function formatPercent(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export interface OrdersReportFiltersState {
  searchValue: string;
  yearFile: string | null;
  providerName: string | null;
  orderStatus: string | null;
  paidAtFrom: Date | null;
  paidAtTo: Date | null;
}

export interface OrdersReportFilterOptions {
  years: string[];
  providers: string[];
  orderStatuses: string[];
}

export interface OrdersReportFilterChip {
  id: keyof OrdersReportFiltersState | 'paidAtRange';
  label: string;
}

export const EMPTY_ORDERS_REPORT_FILTERS: OrdersReportFiltersState = {
  searchValue: '',
  yearFile: null,
  providerName: null,
  orderStatus: null,
  paidAtFrom: null,
  paidAtTo: null
};

export const ORDERS_REPORT_STATUS_OPTIONS = [
  'CONFIRMADO',
  'EN PROCESO',
  'PAGADO',
  'CANCELADO',
  'ELIMINADO',
  'NOTACREDITO'
] as const;

export const ORDERS_REPORT_YEAR_OPTIONS = [
  '2023',
  '2024',
  '2025',
  '2026',
  '2027'
] as const;

export const ORDERS_REPORT_COLUMNS = [
  'fileNumber',
  'numberOS',
  'estadoOS',
  'nameProveedor',
  'serviceType',
  'paymentCondition',
  'scheduledPaymentDate',
  'fechaPago',
  'totalGeneral',
  'currency',
  'actions'
] as const;

function inDateRange(
  value: string | null | undefined,
  from: Date | null,
  to: Date | null
): boolean {
  if (!from && !to) {
    return true;
  }
  if (!value) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  if (from && date < from) {
    return false;
  }
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (date > end) {
      return false;
    }
  }
  return true;
}

export function buildOrdersReportFilterOptions(
  rows: OrdersAvailableReportRow[]
): OrdersReportFilterOptions {
  const list = rows ?? [];
  return {
    years: [...ORDERS_REPORT_YEAR_OPTIONS],
    providers: [
      ...new Set(list.map((row) => row.nameProveedor).filter(Boolean))
    ].sort() as string[],
    orderStatuses: [...ORDERS_REPORT_STATUS_OPTIONS]
  };
}

export function buildOrdersReportFilterChips(
  filters: OrdersReportFiltersState
): OrdersReportFilterChip[] {
  const chips: OrdersReportFilterChip[] = [];

  if (filters.searchValue?.trim()) {
    chips.push({ id: 'searchValue', label: `Buscar: ${filters.searchValue.trim()}` });
  }
  if (filters.yearFile) {
    chips.push({ id: 'yearFile', label: `Año: ${filters.yearFile}` });
  }
  if (filters.providerName) {
    chips.push({ id: 'providerName', label: `Proveedor: ${filters.providerName}` });
  }
  if (filters.orderStatus) {
    chips.push({ id: 'orderStatus', label: `Estado: ${filters.orderStatus}` });
  }
  if (filters.paidAtFrom || filters.paidAtTo) {
    chips.push({ id: 'paidAtRange', label: 'Fecha de pago' });
  }

  return chips;
}

export function countActiveOrdersReportFilters(
  filters: OrdersReportFiltersState
): number {
  return buildOrdersReportFilterChips(filters).length;
}

export function removeOrdersReportFilter(
  filters: OrdersReportFiltersState,
  chipId: OrdersReportFilterChip['id']
): OrdersReportFiltersState {
  switch (chipId) {
    case 'searchValue':
      return { ...filters, searchValue: '' };
    case 'yearFile':
      return { ...filters, yearFile: null };
    case 'providerName':
      return { ...filters, providerName: null };
    case 'orderStatus':
      return { ...filters, orderStatus: null };
    case 'paidAtRange':
      return { ...filters, paidAtFrom: null, paidAtTo: null };
    default:
      return filters;
  }
}

export function filterOrdersReportRows(
  rows: OrdersAvailableReportRow[],
  filters: OrdersReportFiltersState
): OrdersAvailableReportRow[] {
  const search = filters.searchValue?.trim().toLowerCase() ?? '';
  const terms = search ? search.split(/\s+/).filter(Boolean) : [];

  return (rows ?? []).filter((row) => {
    if (terms.length) {
      const haystack = [
        row.numberFile,
        row.fileExtend,
        row.numberOS,
        row.estadoOS,
        row.nameProveedor,
        row.paymentCondition,
        row.currency,
        ...(row.serviceTypes ?? [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!terms.every((term) => haystack.includes(term))) {
        return false;
      }
    }

    if (
      filters.yearFile &&
      !String(row.numberFile ?? '')
        .toLowerCase()
        .includes(filters.yearFile.toLowerCase())
    ) {
      return false;
    }
    if (filters.providerName && row.nameProveedor !== filters.providerName) {
      return false;
    }
    if (filters.orderStatus && row.estadoOS !== filters.orderStatus) {
      return false;
    }
    if (!inDateRange(row.fechaPago, filters.paidAtFrom, filters.paidAtTo)) {
      return false;
    }

    return true;
  });
}

export function formatOrdersReportStatus(
  status: string | null | undefined
): string {
  if (!status) {
    return '-';
  }
  const key = status.toUpperCase();
  if (key === 'SENT') return 'ENVIADO';
  if (key === 'CONFIRMED') return 'CONFIRMADO';
  if (key === 'DENIED') return 'DENEGADO';
  if (key === 'STANDBY') return 'EN ESPERA';
  return status;
}
